import React, { ReactNode, useRef, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { FilledInputProps } from '@mui/material/FilledInput';
import { FormHelperTextProps as FormHelperTextPropsType } from '@mui/material/FormHelperText';
import { InputProps as StandardInputProps } from '@mui/material/Input';
import { InputBaseComponentProps } from '@mui/material/InputBase';
import { InputLabelProps as InputLabelPropsType } from '@mui/material/InputLabel';
import { OutlinedInputProps } from '@mui/material/OutlinedInput';
import TextField from '@mui/material/TextField';

type Props = {
  name: string;
  autoComplete?: string;
  autoFocus?: boolean;
  defaultValue?: string;
  disabled?: boolean;
  FormHelperTextProps?: Partial<FormHelperTextPropsType>;
  fullWidth?: boolean;
  helperText?: ReactNode;
  id?: string;
  InputLabelProps?: Partial<InputLabelPropsType>;
  InputProps?:
    | Partial<StandardInputProps>
    | Partial<FilledInputProps>
    | Partial<OutlinedInputProps>;
  inputProps?: InputBaseComponentProps;
  label?: ReactNode;
  margin?: 'none' | 'dense' | 'normal';
  multiline?: boolean;
  placeholder?: string;
  required?: boolean;
  rows?: string | number;
  rowsMax?: string | number;
  size?: 'small' | 'medium';
  type?: string;
  variant?: 'standard' | 'outlined' | 'filled';
  autoResize?: boolean; // New prop to enable/disable auto-resize
  minRows?: number; // Minimum number of rows
  maxRows?: number; // Maximum number of rows
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

const HookFormTextField = ({
  name,
  autoComplete = 'off',
  autoFocus = false,
  defaultValue = '',
  disabled = false,
  FormHelperTextProps = {},
  fullWidth = true,
  helperText = '',
  id,
  InputLabelProps = {},
  InputProps = {},
  inputProps = {},
  label = '',
  margin = 'normal',
  multiline = false,
  placeholder = '',
  required = false,
  rows = 1,
  rowsMax = 4,
  size = 'medium',
  type = 'text',
  variant = 'outlined',
  autoResize = false, // Default to false for backward compatibility
  minRows = 1,
  maxRows = 10,
  onBlur = () => {},
  onChange = () => {},
}: Props) => {
  const {
    formState: { errors },
  } = useFormContext();
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const error = errors[name];
  const displayError = Boolean(error?.message);

  // Auto-resize function
  const handleAutoResize = () => {
    if (!autoResize || !multiline || !textareaRef.current) return;
    
    const textarea = textareaRef.current;
    
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    
    // Calculate line height
    const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight) || 20;
    const minHeight = lineHeight * minRows;
    const maxHeight = lineHeight * maxRows;
    
    // Set new height based on content, respecting min/max constraints
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;
    
    // Show scrollbar if content exceeds max height
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };

  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onBlur: formOnBlur, onChange: formOnChange, value } }) => {
        // Auto-resize when value changes
        useEffect(() => {
          if (autoResize && multiline) {
            // Small delay to ensure DOM is updated
            setTimeout(handleAutoResize, 0);
          }
        }, [value, autoResize, multiline]);

        return (
          <TextField
            name={name}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            disabled={disabled}
            error={displayError}
            fullWidth={fullWidth}
            helperText={displayError ? String(error.message) : helperText}
            id={id ?? name}
            slotProps={{
              formHelperText: FormHelperTextProps,
              inputLabel: InputLabelProps,
              input: {
                ...InputProps,
                inputRef: autoResize && multiline ? textareaRef : undefined,
                sx: {
                  ...InputProps.sx,
                  ...(autoResize && multiline && {
                    '& textarea': {
                      resize: 'none',
                      overflow: 'hidden',
                    },
                  }),
                },
              },
              htmlInput: inputProps,
            }}
            label={label}
            margin={margin}
            multiline={multiline}
            onBlur={(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              if (onBlur) onBlur(e);
              formOnBlur();
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              if (onChange) onChange(e);
              formOnChange(e);
              // Trigger auto-resize on change
              if (autoResize && multiline) {
                setTimeout(handleAutoResize, 0);
              }
            }}
            placeholder={placeholder}
            required={required}
            rows={autoResize ? minRows : (multiline ? rowsMax : 1)}
            size={size}
            type={type}
            value={value ?? ''}
            variant={variant}
          />
        );
      }}
    />
  );
};

export default HookFormTextField;